import { API_URL } from 'env';

/**
 * API dùng riêng cho màn check-in của khách. Khác các hàm trong src/api: KHÔNG tự đăng xuất
 * khi gặp 401/403 — máy check-in đặt ở sảnh, bật trang đăng nhập trước mặt khách thì máy kẹt.
 * Lỗi phân quyền / hết phiên trả về `authError` để màn hình báo khách liên hệ lễ tân.
 */
export interface KioskResponse<T = any> {
  status: boolean;
  data: T;
  msg: string;
  /** Nguyên response để lấy lỗi chi tiết (extractApiError / isFieldRequiredError) */
  raw: any;
  /** 401 (hết phiên) hoặc 403 (tài khoản thiếu quyền) */
  authError: boolean;
}

const firstError = (json: any) => {
  const err = json?.error ?? json?.errors;
  if (Array.isArray(err)) return err[0];
  if (err && typeof err === 'object') return Object.values(err).flat()[0];
  return err;
};

async function request<T = any>(method: 'GET' | 'POST', path: string, body?: Record<string, any>, multipart = false): Promise<KioskResponse<T>> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  };
  let payload: BodyInit | undefined;
  if (body && multipart) {
    // Giống mixPostV2: bỏ giá trị rỗng, mảng gửi dạng key[]
    const form = new FormData();
    Object.entries(body).forEach(([key, value]) => {
      if (value === null || value === undefined) return;
      if (Array.isArray(value)) value.forEach(item => form.append(`${key}[]`, item));
      else form.append(key, value);
    });
    payload = form;
  } else if (body) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${API_URL}${path}`, { method, headers, body: payload });
    const json = await response.json().catch(() => null);
    const authError = response.status === 401 || response.status === 403;
    return {
      status: response.ok && Boolean(json?.status),
      data: json?.data ?? null,
      msg: json?.msg || firstError(json) || '',
      raw: json,
      authError,
    };
  } catch (error) {
    console.error(`Kiosk ${method} ${path} failed:`, error);
    return { status: false, data: null as T, msg: '', raw: null, authError: false };
  }
}

const query = (params: Record<string, string | number | undefined>) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined) qs.append(k, String(v)); });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

/** Các kịch bản API check-in trả về trong `data.action` */
export type CheckinAction = 'checkin_success' | 'require_confirmation' | 'already_in_progress' | 'suggest_appointment' | 'create_appointment' | 'create_customer';

export interface CheckinTag { id: number; name: string; code?: string | null; color?: string | null }

export interface CheckinResult {
  action?: CheckinAction;
  appointment?: any;
  suggested_appointment?: any;
  expired_appointment?: any;
  customer?: { id: number; fullname?: string; display_name?: string; phone?: string };
  phone?: string;
  /** require_confirmation: thẻ S hiện tại và thẻ sẽ chuyển sang khi xác nhận */
  current_tag?: CheckinTag | null;
  target_tag?: CheckinTag | null;
  /** Lượt server tạo lúc check-in */
  visit?: { id: number; queue: 'consult' | 'exec'; reopened?: boolean; treatment?: { need_choice?: boolean; no_session?: boolean } };
}

/**
 * POST /appointments/checkin — gửi `id` (QR) hoặc `customer_phone`.
 * Lần đầu không gửi `confirm`: lịch cần xác nhận thì server trả `require_confirmation` và chưa đổi gì.
 * Nhân viên xác nhận xong mới gửi lại đúng `id` của lịch đó kèm `confirm: true`.
 */
export const kioskCheckin = (payload: { id?: number | string; customer_phone?: string; confirm?: boolean }) =>
  request<CheckinResult>('POST', '/api/v1/be/appointments/checkin', payload);

/** Số lịch tối đa mỗi trang khi tải danh sách lịch hẹn trong ngày cho màn Lễ tân */
const DAY_PAGE_SIZE = 200;
const DAY_MAX_PAGES = 5;

/**
 * Lịch hẹn có ngày hẹn (`scheduled_at`) trong ngày `ymd`, gom đủ các trang.
 * `date_from` / `date_to` của GET /appointments lọc theo ngày hẹn.
 */
export async function kioskDayAppointments(ymd: string, branchId?: number | null): Promise<KioskResponse<any[]>> {
  const items: any[] = [];
  for (let page = 1; page <= DAY_MAX_PAGES; page++) {
    const res = await request('GET', `/api/v1/be/appointments${query({ date_from: ymd, date_to: ymd, branch_id: branchId ?? undefined, limit: DAY_PAGE_SIZE, page })}`);
    if (!res.status) return { ...res, data: items };
    const list = res.data?.items || res.data?.data || [];
    items.push(...list);
    const total = Number(res.data?.total) || 0;
    if (!list.length || items.length >= total) break;
  }
  return { status: true, data: items, msg: '', raw: null, authError: false };
}

/** Tìm lịch hẹn theo từ khóa; `search` khớp cả SĐT ghi trên lịch hẹn (`customer_phone`) */
export const kioskSearchAppointments = (search: string) =>
  request('GET', `/api/v1/be/appointments${query({ search, limit: 50 })}`);

/** Dịch vụ đang hoạt động (status 0; 1 là đã khóa) */
/** Chỉ dịch vụ đang hoạt động và được gán hiển thị ở Check-in (trang Quản lý Dịch vụ → "Hiển thị ở Check-in") */
export const kioskServices = () =>
  request('GET', `/api/v1/be/services${query({ status: 0, display_area: 'checkin', take: 1000 })}`);

export const kioskBranches = () =>
  request('GET', `/api/v1/be/branches${query({ limit: 100 })}`);

export const kioskCustomerDetail = (id: number) =>
  request('GET', `/api/v1/be/customers/${id}`);

/**
 * POST /customers — multipart giống form "Tạo khách hàng mới" của CMS; SĐT đã có thì API cập nhật khách cũ.
 * `is_auto_staff: 1` để API tự gán Trực page / Telesale theo cài đặt appointment_default_staff (khi đó telesale_id không bắt buộc).
 */
export const kioskCreateCustomer = (payload: Record<string, any>) =>
  request('POST', '/api/v1/be/customers', payload, true);

/** POST /customers/{id} — cập nhật hồ sơ (dùng cho địa chỉ) */
export const kioskUpdateCustomer = (id: number, payload: Record<string, any>) =>
  request('POST', `/api/v1/be/customers/${id}`, payload);

/** PUT /appointments/{id} — dời giờ hẹn (khách đến sớm hơn ngày hẹn), payload giống ô sửa giờ ở chi tiết lịch hẹn */
export const kioskReschedule = (id: number | string, payload: { customer_id?: number; scheduled_at: string; branch_id?: number }) =>
  request('PUT', `/api/v1/be/appointments/${id}`, payload);

/** POST /appointments — payload giống trang Tạo lịch hẹn */
export const kioskCreateAppointment = (payload: Record<string, any>) =>
  request('POST', '/api/v1/be/appointments', payload);
